import React, { useEffect, useRef, useState } from 'react';
import { css, cx } from '@emotion/css';
import { TextInput } from '../../shared/components/TextInput';
import { FormProvider, useForm } from 'react-hook-form';
import {
  CurrencyAmountCard,
  selectedCurrencyCardStyle,
  unselectedCurrencyCardStyle,
} from './CurrencyAmountCard';
import {
  CheckCircleOutline,
  CheckCircleSolidInverted,
} from '../../shared/assets/icons';
import { Colors } from '../../shared/styles/colors';

enum AmountOptionsEnum {
  FIRST = 1,
  SECOND = 2,
  THIRD = 3,
  OTHER_AMOUNT = 4,
}

interface ICurrencyAmountSelectionProps {
  setAmount: (amount: string) => void;
}

export const CurrencyAmountSelection = (
  props: ICurrencyAmountSelectionProps,
): JSX.Element => {
  const { setAmount } = props;
  const [selectedOption, setSelectedOption] = useState<AmountOptionsEnum>();
  const methods = useForm({
    defaultValues: {
      amount: '',
    },
  });
  const customAmountRef = useRef<HTMLDivElement>(null);

  // The custom amount field appears below the fold and can end up hidden
  // behind the sticky "proceed to pay" bar, so bring it into view.
  useEffect(() => {
    if (selectedOption === AmountOptionsEnum.OTHER_AMOUNT) {
      customAmountRef.current?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
  }, [selectedOption]);

  return (
    <FormProvider {...methods}>
      <form
        className={formStyle}
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <div
          className={currencyAmountSelectionStyle}
          role="radiogroup"
          aria-label="Amount"
        >
          <CurrencyAmountCard
            onClick={() => {
              (setSelectedOption(AmountOptionsEnum.FIRST), setAmount('20'));
            }}
            amount={20}
            isSelected={selectedOption === AmountOptionsEnum.FIRST}
          />
          <CurrencyAmountCard
            onClick={() => {
              (setSelectedOption(AmountOptionsEnum.SECOND), setAmount('50'));
            }}
            amount={50}
            isSelected={selectedOption === AmountOptionsEnum.SECOND}
          />
          <CurrencyAmountCard
            onClick={() => {
              (setSelectedOption(AmountOptionsEnum.THIRD), setAmount('100'));
            }}
            amount={100}
            isSelected={selectedOption === AmountOptionsEnum.THIRD}
          />
          {selectedOption === AmountOptionsEnum.OTHER_AMOUNT ? (
            <button
              type="button"
              role="radio"
              aria-checked="true"
              className={cx(
                unselectedCurrencyCardStyle,
                selectedCurrencyCardStyle,
              )}
              onClick={() => setSelectedOption(AmountOptionsEnum.OTHER_AMOUNT)}
            >
              <div>
                <span>Other amount</span>
                <CheckCircleSolidInverted
                  className={cx(iconStyle, circleSolidInvertedStyle)}
                />
              </div>
            </button>
          ) : (
            <button
              type="button"
              role="radio"
              aria-checked="false"
              className={unselectedCurrencyCardStyle}
              onClick={() => {
                setSelectedOption(AmountOptionsEnum.OTHER_AMOUNT);
                setAmount(methods.getValues('amount'));
              }}
            >
              <div>
                <span>Other amount</span>
                <CheckCircleOutline className={iconStyle} />
              </div>
            </button>
          )}
          {selectedOption === AmountOptionsEnum.OTHER_AMOUNT && (
            <div ref={customAmountRef} className={customAmountInputStyle}>
              <TextInput
                name={'amount'}
                placeHolder={'enter an amount'}
                type={'text'}
                onChange={setAmount}
                autoFocus
              />
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

const formStyle = css`
  width: 100%;
`;

const currencyAmountSelectionStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  width: 100%;
`;

const customAmountInputStyle = css`
  grid-column: span 2;
  margin-top: 0.25rem;
`;

const iconStyle = css`
  min-width: 1.5rem;
  min-height: 1.5rem;
  align-self: flex-start;
`;

const circleSolidInvertedStyle = css`
  fill: ${Colors.WHITE};
`;
